/**
 * Wizard för användare utan Tibber och utan CSV-export.
 *
 * Frågar minimalt antal saker för att kunna syntetisera en timserie:
 * prisområde, årsförbrukning, värmekälla, sol, EV. Sista steget visar
 * en sammanfattning och konfidensvarning innan användaren går vidare
 * till simulatorn.
 */

import { useState, useEffect, useRef } from "react";
import {
  generateHoursFromWizard,
  type WizardInput,
  type HeatingType,
  type GenerateResult,
} from "../data-sources/wizardGenerator";
import { enrichCsvHours, ApiError } from "../api/client";
import type { UserData, PriceArea } from "../types";
import { BatteryLoader } from "./BatteryLoader";
import { useAnalytics } from "../hooks/useAnalytics";

interface WizardProps {
  onDataLoaded: (data: UserData) => void;
  onCancel: () => void;
}

type Step = "intro" | "questions" | "review" | "enriching" | "error";

const HEATING_LABELS: Record<HeatingType, { title: string; subtitle: string }> = {
  apartment: {
    title: "Lägenhet",
    subtitle: "Bara hushållsel, värme ingår i hyran",
  },
  district_heating: {
    title: "Villa med fjärrvärme",
    subtitle: "Bara hushållsel, värme via fjärrvärmecentral",
  },
  heat_pump_ground: {
    title: "Villa med bergvärme",
    subtitle: "Värmepump i borrhål, jämn säsongskurva",
  },
  heat_pump_air: {
    title: "Villa med luftvärmepump",
    subtitle: "Luft-luft eller luft-vatten, sämre vid sträng kyla",
  },
  direct_electric: {
    title: "Villa med direktverkande el",
    subtitle: "Element eller elpanna, stor vintertopp",
  },
};

export default function Wizard({ onDataLoaded, onCancel }: WizardProps) {
  const [step, setStep] = useState<Step>("questions");
  const [priceArea, setPriceArea] = useState<PriceArea>("SE3");
  // Numeriska fält tillåter tom sträng så användaren kan rensa och skriva
  // om från noll. parseInt på tomt fält gav annars "0" som klistrade sig.
  const [annualKWh, setAnnualKWh] = useState<number | "">(15000);
  const [heatingType, setHeatingType] = useState<HeatingType>("heat_pump_air");
  const [hasSolar, setHasSolar] = useState<boolean>(false);
  const [solarAnnualKWh, setSolarAnnualKWh] = useState<number | "">("");
  const [hasEv, setHasEv] = useState<boolean>(false);
  const [evKmPerYear, setEvKmPerYear] = useState<number | "">("");
  const [postalCode, setPostalCode] = useState<string>("");
  const [generated, setGenerated] = useState<GenerateResult | null>(null);
  const [lastInput, setLastInput] = useState<WizardInput | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Analytics-funnel: started vid mount, abandoned vid unmount om hen inte
  // slutförde flödet, completed när Använd-knappen klickas.
  const { trackWizardFunnel } = useAnalytics(null);
  const startTimeMs = useRef<number>(Date.now());
  const completedRef = useRef<boolean>(false);

  useEffect(() => {
    trackWizardFunnel("started");
    return () => {
      if (!completedRef.current) {
        trackWizardFunnel("abandoned", Date.now() - startTimeMs.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ANNUAL_KWH_MIN = 1500;
  const ANNUAL_KWH_MAX = 35000;
  const SOLAR_KWH_MIN = 500;
  const SOLAR_KWH_MAX = 25000;
  const EV_KM_MIN = 1000;
  const EV_KM_MAX = 50000;

  // Hjälp: parsa input-värde till number eller "" så vi kan rensa fältet
  function parseInput(value: string): number | "" {
    if (value === "") return "";
    const n = parseInt(value, 10);
    return isNaN(n) ? "" : n;
  }

  // Hjälp: kontrollera om ett numeriskt fält är ifyllt och inom range
  function isOutOfRange(
    value: number | "",
    min: number,
    max: number,
  ): boolean {
    if (value === "") return true;
    return value < min || value > max;
  }

  const annualOutOfRange = isOutOfRange(
    annualKWh,
    ANNUAL_KWH_MIN,
    ANNUAL_KWH_MAX,
  );
  const solarOutOfRange =
    hasSolar && isOutOfRange(solarAnnualKWh, SOLAR_KWH_MIN, SOLAR_KWH_MAX);
  const evOutOfRange =
    hasEv && isOutOfRange(evKmPerYear, EV_KM_MIN, EV_KM_MAX);
  const hasValidationError =
    annualOutOfRange || solarOutOfRange || evOutOfRange;

  function handleGenerate() {
    if (hasValidationError) return;
    // Två-siffrigt prefix för länsnivå, om angivet och giltigt
    const prefix = postalCode.replace(/\D/g, "").slice(0, 2);
    const input: WizardInput = {
      priceArea,
      annualKWh: typeof annualKWh === "number" ? annualKWh : 0,
      heatingType,
      hasSolar,
      solarAnnualKWh:
        hasSolar && typeof solarAnnualKWh === "number" ? solarAnnualKWh : 0,
      hasEv,
      evKmPerYear:
        hasEv && typeof evKmPerYear === "number" ? evKmPerYear : 0,
      ...(prefix.length === 2 && { postalCodePrefix: prefix }),
    };
    const result = generateHoursFromWizard(input);
    setGenerated(result);
    setLastInput(input);
    setStep("review");
  }

  async function handleConfirm() {
    if (!generated) return;
    setStep("enriching");
    setError(null);
    try {
      const enriched = await enrichCsvHours(
        priceArea,
        generated.hours.map((h) => ({
          from: h.from,
          importKWh: h.importKWh,
          exportKWh: h.exportKWh,
        })),
      );

      const userData: UserData = {
        metadata: {
          source: "profile-estimate",
          fetchedAt: new Date().toISOString(),
          resolution: enriched.hours[0]?.quarterPrices ? "quarter" : "hour",
          // 'modeled' markerar att datan är syntetisk — Simulator kan
          // visa disclaimer baserat på detta (kommer i nästa iteration).
          quality: "modeled",
          periodFrom: enriched.metadata.firstTimestamp,
          periodTo: enriched.metadata.lastTimestamp,
          hourCount: enriched.metadata.totalHours,
          totalImport: generated.diagnostics.actualAnnualImportKWh,
          totalExport: generated.diagnostics.actualAnnualExportKWh,
          hoursWithMissingPrice: enriched.metadata.hoursWithMissingPrice,
          tibberHome: {
            id: `wizard-${crypto.randomUUID()}`,
            nickname: null,
            address: { address1: null, postalCode: null, city: null },
            gridArea: null,
            priceArea,
          },
          ...(lastInput && { wizardInput: lastInput }),
        },
        hours: enriched.hours.map((h) => ({
          from: h.from,
          importKWh: h.importKWh,
          exportKWh: h.exportKWh,
          spotPrice: h.spotPrice,
          ...(h.quarterPrices && { quarterPrices: h.quarterPrices }),
        })),
      };

      completedRef.current = true;
      trackWizardFunnel("completed", Date.now() - startTimeMs.current);
      onDataLoaded(userData);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Kunde inte hämta spotpriser. Försök igen om en stund.";
      setError(message);
      setStep("error");
    }
  }

  if (step === "intro") {
    return (
      <section className="wizard">
        <h2>Mata in din profil</h2>
        <p className="wizard-lead">
          Du svarar på fem frågor om ditt hem och din elförbrukning. Vi
          räknar fram en realistisk årsprofil och simulerar vad ett batteri
          skulle ge dig.
        </p>
        <p className="hint">
          Resultatet är ungefärligt och bygger på typvärden för svenska
          hushåll. Bra för att jämföra batteristorlekar och få en grov
          uppfattning om återbetalning, men enskilda kronor kan skifta
          ±20% mot ditt verkliga utfall.
        </p>
        <div className="wizard-actions">
          <button type="button" onClick={() => setStep("questions")}>
            Starta
          </button>
          <button type="button" className="link" onClick={onCancel}>
            Avbryt
          </button>
        </div>
      </section>
    );
  }

  if (step === "questions") {
    return (
      <section className="wizard">
        <h2>Berätta om ditt hem</h2>

        <div className="wizard-field">
          <label htmlFor="wz-area">Prisområde</label>
          <select
            id="wz-area"
            value={priceArea}
            onChange={(e) => setPriceArea(e.target.value as PriceArea)}
          >
            <option value="SE1">SE1 Norra Norrland</option>
            <option value="SE2">SE2 Norra Sverige</option>
            <option value="SE3">SE3 Mellersta Sverige</option>
            <option value="SE4">SE4 Södra Sverige</option>
          </select>
          <span className="wizard-help">
            Står på din elräkning. Bestämmer vilka spotpriser vi använder.
          </span>
        </div>

        <div className="wizard-field">
          <label htmlFor="wz-postal">Postnummer (frivilligt)</label>
          <input
            id="wz-postal"
            type="text"
            inputMode="numeric"
            maxLength={5}
            placeholder="t.ex. 41501"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            style={{ maxWidth: "200px" }}
          />
          <span className="wizard-help">
            Hjälper oss göra kalkylen mer exakt för ditt område. Frivilligt.
          </span>
        </div>

        <div className="wizard-field">
          <label htmlFor="wz-annual">Total elförbrukning per år</label>
          <div className="wizard-input-with-unit">
            <input
              id="wz-annual"
              type="number"
              min={ANNUAL_KWH_MIN}
              max={ANNUAL_KWH_MAX}
              step={500}
              value={annualKWh}
              onChange={(e) => setAnnualKWh(parseInput(e.target.value))}
            />
            <span>kWh</span>
          </div>
          <span className="wizard-help">
            Inkluderar uppvärmning, hushållsel och ev. EV-laddning. Står
            längst ner på elräkningen. Typvärden: lägenhet 2000-5000,
            villa fjärrvärme 5000-8000, villa värmepump 12000-20000,
            villa direktel 20000-30000.
          </span>
          {annualOutOfRange && (
            <span className="wizard-error">
              Värdet bör ligga mellan{" "}
              {ANNUAL_KWH_MIN.toLocaleString("sv-SE")} och{" "}
              {ANNUAL_KWH_MAX.toLocaleString("sv-SE")} kWh/år för att profilen
              ska bli meningsfull.
            </span>
          )}
        </div>

        <div className="wizard-field">
          <span className="wizard-label">Hur värms hemmet?</span>
          <div className="wizard-radio-grid">
            {(Object.keys(HEATING_LABELS) as HeatingType[]).map((key) => (
              <label
                key={key}
                className={`wizard-radio-card ${
                  heatingType === key ? "selected" : ""
                }`}
              >
                <input
                  type="radio"
                  name="heating"
                  value={key}
                  checked={heatingType === key}
                  onChange={() => setHeatingType(key)}
                />
                <strong>{HEATING_LABELS[key].title}</strong>
                <span>{HEATING_LABELS[key].subtitle}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="wizard-field">
          <label className="wizard-checkbox">
            <input
              type="checkbox"
              checked={hasSolar}
              onChange={(e) => {
                setHasSolar(e.target.checked);
                if (e.target.checked && solarAnnualKWh === "") {
                  setSolarAnnualKWh(8000);
                }
              }}
            />
            <span>Jag har solpaneler</span>
          </label>
          {hasSolar && (
            <div className="wizard-conditional">
              <label htmlFor="wz-solar">Solens årsproduktion</label>
              <div className="wizard-input-with-unit">
                <input
                  id="wz-solar"
                  type="number"
                  min={500}
                  max={25000}
                  step={500}
                  value={solarAnnualKWh}
                  onChange={(e) =>
                    setSolarAnnualKWh(parseInput(e.target.value))
                  }
                />
                <span>kWh/år</span>
              </div>
              <span className="wizard-help">
                Vanlig villaanläggning: 5-12 kW ger 5000-12000 kWh/år. Står
                ofta på elräkningen eller i installatörens prognos.
              </span>
              {solarOutOfRange && (
                <span className="wizard-error">
                  Värdet bör ligga mellan {SOLAR_KWH_MIN} och {SOLAR_KWH_MAX}{" "}
                  kWh/år.
                </span>
              )}
            </div>
          )}
        </div>

        <div className="wizard-field">
          <label className="wizard-checkbox">
            <input
              type="checkbox"
              checked={hasEv}
              onChange={(e) => {
                setHasEv(e.target.checked);
                if (e.target.checked && evKmPerYear === "") {
                  setEvKmPerYear(15000);
                }
              }}
            />
            <span>Jag laddar elbil hemma</span>
          </label>
          {hasEv && (
            <div className="wizard-conditional">
              <label htmlFor="wz-ev">Körsträcka per år</label>
              <div className="wizard-input-with-unit">
                <input
                  id="wz-ev"
                  type="number"
                  min={1000}
                  max={50000}
                  step={1000}
                  value={evKmPerYear}
                  onChange={(e) =>
                    setEvKmPerYear(parseInput(e.target.value))
                  }
                />
                <span>km/år</span>
              </div>
              <span className="wizard-help">
                Vi antar smart laddning nattetid (23-06) med 0.18 kWh/km
                inklusive laddförluster.
              </span>
              {evOutOfRange && (
                <span className="wizard-error">
                  Värdet bör ligga mellan {EV_KM_MIN.toLocaleString("sv-SE")} och{" "}
                  {EV_KM_MAX.toLocaleString("sv-SE")} km/år.
                </span>
              )}
            </div>
          )}
        </div>

        <div className="wizard-actions">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={hasValidationError}
          >
            Granska profil
          </button>
          <button
            type="button"
            className="link"
            onClick={onCancel}
          >
            Avbryt
          </button>
        </div>
      </section>
    );
  }

  if (step === "review" && generated) {
    const { diagnostics } = generated;
    return (
      <section className="wizard">
        <h2>Din syntetiska profil</h2>
        <p className="hint">
          Så här ser ditt år ut enligt din inmatning. Kontrollera att
          siffrorna känns rimliga innan du går vidare.
        </p>

        <dl className="wizard-summary">
          <dt>Prisområde</dt>
          <dd>{priceArea}</dd>

          <dt>Importerad el (köpt från nätet)</dt>
          <dd>
            {diagnostics.actualAnnualImportKWh.toLocaleString("sv-SE")} kWh/år
          </dd>

          {diagnostics.actualAnnualExportKWh > 0 && (
            <>
              <dt>Exporterad sol (såld till nätet)</dt>
              <dd>
                {diagnostics.actualAnnualExportKWh.toLocaleString("sv-SE")}{" "}
                kWh/år
              </dd>
            </>
          )}

          <dt>Vintermånaderna (dec-feb)</dt>
          <dd>
            {Math.round(diagnostics.winterImportShare * 100)}% av årsimporten
          </dd>

          <dt>Värmekälla</dt>
          <dd>{HEATING_LABELS[heatingType].title}</dd>
        </dl>

        <p className="hint warning">
          <strong>Viktigt om resultatet:</strong> Profilen är beräknad utifrån
          dina svar och typvärden för svenska hushåll, inte uppmätta data
          från ditt eget hem. Användbart för att jämföra batteristorlekar
          och få en grov uppfattning, men enskilda kronor kan avvika ±20%
          mot ditt faktiska utfall.
        </p>

        <div className="wizard-actions">
          <button type="button" onClick={handleConfirm}>
            Använd denna profil
          </button>
          <button
            type="button"
            className="link"
            onClick={() => setStep("questions")}
          >
            Ändra svar
          </button>
        </div>
      </section>
    );
  }

  if (step === "enriching") {
    return (
      <section className="wizard wizard-loading">
        <BatteryLoader size={120} label="Hämtar spotpriser" />
        <p className="hint">
          Bygger din profil mot spotpriser för {priceArea}. Tar några
          sekunder.
        </p>
      </section>
    );
  }

  // error
  return (
    <section className="wizard">
      <h2>Något gick fel</h2>
      <p className="error">{error}</p>
      <button type="button" onClick={() => setStep("review")}>
        Försök igen
      </button>
      <button type="button" className="link" onClick={onCancel}>
        Avbryt
      </button>
    </section>
  );
}
